import BackButton from "components/Button/BackButton"
import DeleteButton from "components/Button/DeleteButton"
import SaveButton from "components/Button/SaveButton"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { EmptyString, FormTypeAdd, FormTypeEdit, StatusCodeActive, StatusCodeDelete } from "utils/Constant"
import moment from "moment"
import { GetStatus, removeNonNumeric } from "utils/helpers"
import UnDeleteButton from "components/Button/UnDeleteButton"
import { useModal } from "utils/context/ModalContext"
import { LocationService } from "services/LocationService/LocationService"
import { MainBodyCard } from "components/Cards"

export default function LocationForm(){
    // On Load

    useEffect(() => {
        const onLoad = async () => {
            if (formType === FormTypeEdit){
                showLoading()

                try {
                    const res = await fetchLocationById(locationId)
                    if (!res) return

                    const data = res.data
                    setLocationName(data.locationName)
                    setAddress(data.address)
                    setCity(data.city)
                    setProvince(data.province)
                    setPhone(data.phone)
                    setCreateDate(data.createDate)
                    setStatus(data.status)
                    setUpdateDate(data.updateDate)
                    setUpdateBy(data.updateBy)
                }
                finally {
                    hideLoading()
                }
            }
        }

        onLoad()
    }, [])

    // Navigate

    const navigate = useNavigate()

    // API 

    const { fetchLocationById, fetchInsertLocation, fetchUpdateLocation, fetchDeleteLocation } = LocationService()

    // Query String

    let queryString = new URLSearchParams(window.location.search)
    let formType = queryString.get("formType")
    let locationId = queryString.get("locationId")

    // Modal

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()

    // Data 

    const [locationName, setLocationName] = useState(EmptyString)
    const [address, setAddress] = useState(EmptyString)
    const [city, setCity] = useState(EmptyString)
    const [province, setProvince] = useState(EmptyString)
    const [phone, setPhone] = useState(EmptyString)
    const [createDate, setCreateDate] = useState(EmptyString)
    const [status, setStatus] = useState(EmptyString)
    const [updateDate, setUpdateDate] = useState(EmptyString)
    const [updateBy, setUpdateBy] = useState(EmptyString)

    // Handle Change

    const locationNameChange = (e) => { setLocationName(e.target.value.toUpperCase()) }
    const addressChange = (e) => { setAddress(e.target.value.toUpperCase()) }
    const cityChange = (e) => { setCity(e.target.value.toUpperCase()) }
    const provinceChange = (e) => { setProvince(e.target.value.toUpperCase()) }
    const phoneChange = (e) => { setPhone(removeNonNumeric(e.target.value)) }

    // Error Validation

    const [errorLocationName, setErrorLocationName] = useState(false)
    const [errorAddress, setErrorAddress] = useState(false)
    const [errorCity, setErrorCity] = useState(false)
    const [errorProvince, setErrorProvince] = useState(false)
    const [errorPhone, setErrorPhone] = useState(false)

    // Function Validation

    const validateForm = () => {
        let isValid = true

        if (locationName === EmptyString){
            setErrorLocationName(true)
            isValid = false
        }else{
            setErrorLocationName(false)
        }
        if (address === EmptyString){
            setErrorAddress(true)
            isValid = false
        }else{
            setErrorAddress(false)
        }
        if (city === EmptyString){
            setErrorCity(true)
            isValid = false
        }else{
            setErrorCity(false)
        }
        if (province === EmptyString){
            setErrorProvince(true)
            isValid = false
        }else{
            setErrorProvince(false)
        }
        if (phone === EmptyString){
            setErrorPhone(true)
            isValid = false
        }else{
            setErrorPhone(false)
        }

        return isValid
    }

    // Button Click

    const saveBtnClick = async () => {
        if (validateForm()){
            const isConfirmed = await confirm()

            if (isConfirmed) {
                showLoading()

                try {
                    if (formType === FormTypeAdd){
                        const res = await fetchInsertLocation(locationName, address, city, province, phone)
                        if (!res) return
                    }else{
                        const res = await fetchUpdateLocation(locationId, locationName, address, city, province, phone)
                        if (!res) return
                    }

                    showSuccess({
                        onSuccess: () => {
                            navigate('/setting/location')
                        }
                    })
                } finally {
                    hideLoading()
                }
            }
        }
    }
    const deleteBtnClick = async () => {
        const isConfirmed = await confirm()

        if (isConfirmed) {
            showLoading()

            try {
                const res = await fetchDeleteLocation(locationId, status)
                if (!res) return

                showSuccess({
                    onSuccess: () => {
                        navigate('/setting/location')
                    }
                })
            } finally {
                hideLoading()
            }
        }
    }
    const backBtnClick = () => {
        navigate('/setting/location')
    }

    return (
        <MainBodyCard>
            <div>
                <div className="flex justify-between">
                    <span className="font-semibold text-2xl">Location Detail</span>
                    <div className={`flex gap-2 items-end justify-end ${ formType === FormTypeAdd ? 'hidden' : 'block'}`}>
                        <span className="text-sm opacity-60">
                            Status : {GetStatus(status)} | Created Date : {moment(createDate).format("DD MMM yyyy")} | Last Updated : {moment(updateDate).format("DD MMM yyyy")} | Updated By : {updateBy}
                        </span>
                    </div>
                </div>
            </div>
            <hr />

            <div className="mt-5 flex flex-col gap-3">
                {/* Location Name */}
                <div>
                    <div className="flex">
                        <div className="flex flex-col sm:flex-row sm:items-center w-full">
                            <div className="w-48">
                                <span>Location Name</span>
                            </div>
                            <div className="px-4 hidden sm:block">
                                <span>:</span>
                            </div>
                            <div className="w-full sm:w-72">
                                <input className="input" value={locationName} onChange={locationNameChange} maxLength={100} />
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center ${ errorLocationName ? 'block' : 'hidden' }`}>
                        <div className="w-48" />
                        <div className="w-9" />
                        <span className="text-red-500">Location Name is required!</span>
                    </div>
                </div>

                {/* Address */}
                <div>
                    <div className="flex">
                        <div className="flex flex-col sm:flex-row sm:items-center w-full">
                            <div className="w-48">
                                <span>Address</span>
                            </div>
                            <div className="px-4 hidden sm:block">
                                <span>:</span>
                            </div>
                            <div className="w-full sm:w-72">
                                <input className="input" value={address} onChange={addressChange} maxLength={200} />
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center ${ errorAddress ? 'block' : 'hidden' }`}>
                        <div className="w-48" />
                        <div className="w-9" />
                        <span className="text-red-500">Address is required!</span>
                    </div>
                </div>

                {/* City */}
                <div>
                    <div className="flex">
                        <div className="flex flex-col sm:flex-row sm:items-center w-full">
                            <div className="w-48">
                                <span>City</span>
                            </div>
                            <div className="px-4 hidden sm:block">
                                <span>:</span>
                            </div>
                            <div className="w-full sm:w-72">
                                <input className="input" value={city} onChange={cityChange} maxLength={50} />
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center ${ errorCity ? 'block' : 'hidden' }`}>
                        <div className="w-48" />
                        <div className="w-9" />
                        <span className="text-red-500">City is required!</span>
                    </div>
                </div>

                {/* Province */}
                <div>
                    <div className="flex">
                        <div className="flex flex-col sm:flex-row sm:items-center w-full">
                            <div className="w-48">
                                <span>Province</span>
                            </div>
                            <div className="px-4 hidden sm:block">
                                <span>:</span>
                            </div>
                            <div className="w-full sm:w-72">
                                <input className="input" value={province} onChange={provinceChange} maxLength={50} />
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center ${ errorProvince ? 'block' : 'hidden' }`}>
                        <div className="w-48" />
                        <div className="w-9" />
                        <span className="text-red-500">Province is required!</span>
                    </div>
                </div>

                {/* Phone */}
                <div>
                    <div className="flex">
                        <div className="flex flex-col sm:flex-row sm:items-center w-full">
                            <div className="w-48">
                                <span>Phone</span>
                            </div>
                            <div className="px-4 hidden sm:block">
                                <span>:</span>
                            </div>
                            <div className="w-full sm:w-72">
                                <input className="input" value={phone} onChange={phoneChange} maxLength={20} />
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center ${ errorPhone ? 'block' : 'hidden' }`}>
                        <div className="w-48" />
                        <div className="w-9" />
                        <span className="text-red-500">Phone is required!</span>
                    </div>
                </div>
            </div>
                
            <div className="mt-5 flex flex-wrap justify-end w-full gap-2">
                <SaveButton onClick={saveBtnClick} visible={true} />
                <DeleteButton onClick={deleteBtnClick} visible={formType === FormTypeAdd ? false : (status === StatusCodeActive ? true : false)} />
                <UnDeleteButton onClick={deleteBtnClick} visible={formType === FormTypeEdit ? (status === StatusCodeDelete ? true : false) : false} />
                <BackButton onClick={backBtnClick} />
            </div>
        </MainBodyCard>
    )
}